// A player's place on his club's depth chart, as a row says it (E1, B-479).
//
// THE BIG SITES' SHORTHAND, "RB2", in a column of its own headed Depth, so a
// list can be put in its order. It was spelled under the name ("Second
// string at RB") until a column was asked for; the head and the tip keep it
// apart from the position rank under the projection ("RB12", `rankText`).

import { useMemo } from 'react';
import { useActiveLeague } from '../../data/LeaguesProvider';
import { clubLabel, getSport } from '../../sports/registry';
import { ordinalOf } from './rank';

export interface DepthPlace {
  position: string;
  rank: number;
}

/** "RB2", "FB1", "RCB1": the chart's column in its own word, and his rank there. */
export function depthTag(d: DepthPlace): string {
  return `${d.position}${d.rank}`;
}

/**
 * "2nd on PIT’s depth chart at RB (running back)." THE WORD SPELLED OUT, for a
 * reader who does not know what PK stands for: the sport's name for the
 * position the chart's word is, or where the chart has a finer word than the
 * sport's positions (PK, FB, RCB) the name of the player's own position —
 * "PK (kicker)", "FB (running back)". Neither known, the word alone.
 */
export function depthTip(
  d: DepthPlace,
  club: string | null,
  names: ReadonlyMap<string, string>,
  position: string | undefined,
): string {
  const name = names.get(d.position) ?? (position === undefined ? undefined : names.get(position));
  return `${ordinalOf(d.rank)} on ${club === null ? 'his club’s' : `${clubLabel(club)}’s`} depth chart at ${d.position}${name === undefined ? '' : ` (${name.toLowerCase()})`}.`;
}

/** The active league's sport's names for its positions, by id: "QB" → "Quarterback". */
export function usePositionNames(): ReadonlyMap<string, string> {
  const sport = useActiveLeague()?.league.sport;
  return useMemo(() => {
    if (sport === undefined) return new Map();
    try {
      return new Map(getSport(sport as never).positions.map((p) => [p.id, p.name]));
    } catch {
      return new Map();
    }
  }, [sport]);
}

/**
 * The active league's sport's name for its pro league ("NFL"), or empty. The
 * clubs themselves are the server's (`PlayersPage.clubs`): the data spells
 * some differently from the sport's own list (nflverse's Rams are `LA`).
 */
export function useProLeague(): string {
  const sport = useActiveLeague()?.league.sport;
  if (sport === undefined) return '';
  try {
    return getSport(sport as never).proLeagueName;
  } catch {
    return '';
  }
}
